import { Fragment, useCallback, useEffect, useRef, useState } from 'react'

import { useRouter } from 'next/router'
import { toJpeg } from 'html-to-image'

import Activities from '~/bootcamp/Activities/Activities'
import useGetAvailableRewards from '~/bootcamp/hooks/useGetAvailableRewards'
import useGetCurrentDifficultyLevel from '~/bootcamp/hooks/useGetCurrentDifficultyLevel'
import useGetCurrentRewardLevel from '~/bootcamp/hooks/useGetCurrentRewardLevel'
import bootcampAnalytics from '~/analytics/bootcamp/bootcampAnalytics'
import { BCAEvent } from '~/analytics/bootcamp/bootcampEvents.types'
import { BlockContent } from '~/components/BlockContent'
import { ToggleButton } from '~/components/ToggleButton'
import { isCheatFunctionalityEnabled } from '~/config/utils'

import { getFirebase } from '~/firebase'

import { isAuthenticated, selectUserId } from '~/auth/slices/auth.slice'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { useSetBootcampDataMutation } from '~/bootcamp/apis/set-data.api'
import { getActivityDiffLevelsCount, getActivityProperty } from '~/bootcamp/pages/BootcampView/BootcampView.utils'
import {
  incrementCorrectConsecutiveAnswersActionCreator,
  incrementDifficultyLevelActionCreator,
  incrementRewardLevelActionCreator,
  resetCorrectConsecutiveAnswersActionCreator,
  selectAnswerState,
  selectBootcampActivityEvents,
  selectCorrectConsecutiveAnswers,
  selectCurrentActivityType,
  selectDidUpdateEvent,
  selectShouldDisableUserInteraction,
  selectShouldGetEnergyOrbs,
  setAnswerStateActionCreator,
  setBootcampActivityEventsActionCreator,
  setCurrentRoundActionCreator,
  setCollectedRewardsCreator,
  setCurrDifficultyLevelActionCreator,
  setCurrRewardLevelActionCreator,
  setDidUpdateEventActionCreator,
  setShouldGetEnergyOrbsActionCreator,
  selectCurrentRound,
  selectShouldGetActivityHint,
  setShouldGetActivityHintActionCreator,
  selectShouldAutoValidateAnswer,
  setShouldAutoValidateAnswerActionCreator,
  setKudosActionCreator,
} from '~/bootcamp/slices/bootcamp-activity.slice'

import {
  setNextBootcampPageActionCreator,
  setRewardsCountActionCreator,
} from '~/gamification/slices/gamification.slice'

import useGetCollectedRewards from '~/bootcamp/hooks/useGetCollectedRewards'
import useGetGemType from '~/bootcamp/hooks/useGetGemType'
import useGetActivityWorkloadLevel from '~/bootcamp/hooks/useGetActivityWorkloadLevel'
import useWillGetEnergyOrbs from '~/bootcamp/hooks/useWillGetEnergyOrbs'
import { selectBootcampPageActivities } from '~/bootcamp/slices/bootcamp-page.slice'
import { selectVideoTopicColor, selectVccComplexityLevel } from '~/bootcamp/slices/vcc-data.slice'
import styles from '~/shared/styles/SanityBlock.module.css'
import { ActivityQuestionType } from '~/types/bootcamp'
import {
  BootcampActivityEventId,
  BootcampActivityRoundEventId,
  BootcampKey,
  BootcampPageUrlParamsType,
  LevelActionType,
} from '~/bootcamp/types/bootcamp.types'
import { useBootcampActivityLogging } from '~/bootcamp/hooks/useBootcampActivityLogging'
import {
  constructBootcampActivityEventId,
  getCurrentRoundFromRoundEvents,
  getXPCountInChest,
} from '~/bootcamp/utils/bootcamp.utils'
import { useGetActivityReward } from '~/bootcamp/Activities/hooks/useGetActivityReward'

import useActivityHint from '~/aioracle/hooks/useActivityHint'
import { useSetActivityRoundMutation } from '~/redux/apis/user-stats-api'

import Loader from '~/components/Utility/Loader'

import { Labels } from '~/analytics/types/analytics.type'

import { CategoryColorClasses } from '~/bootcamp/constants/bootcamp.const'

import useShowRoundSummary from '~/bootcamp/hooks/useShowRoundSummary'
import { setHideHorizontalMenuActionCreator } from '~/redux/slices/menu'
import FeedbackButton from '~/user-feedback/components/FeedbackButton'

import { FeedbackProducts } from '~/user-feedback/constants/userFeedback.const'

import useIsBootcampGradePage from '~/bootcamp/hooks/useIsBootcampGradePage'

import useNextActivityType from '~/bootcamp/hooks/useNextActivityType'

import { REWARD_FEATURE } from '~/gamification/types/gamification.types'
import { setChecklistNotification } from '~/redux/slices/checklist'

import { setShowFreemiumModalCreator } from '~/redux/slices/modals'

import SanityActivityRenderer from './SanityActivityRenderer'
import CheckAnswerAndClearButton from './CheckAnswerAndClearButton'
import ActivitySetCompleted from './ActivitySetCompleted'
import { NextLevel } from './NavigationButton'
import ProgressBarChest from './ProgressBarChest'

export default function ActivitiesWrapper(): React.ReactElement {
  const userId = useAppSelector(selectUserId)

  const [setBootcampData] = useSetBootcampDataMutation()
  const sendToOracleAI = useActivityHint()

  const { query: urlQuery, asPath } = useRouter()
  const { action, key: bootcampKey, level, moduleKey, subtopic } = urlQuery as unknown as BootcampPageUrlParamsType
  const activities = useAppSelector(selectBootcampPageActivities) || []
  const activityHint = useAppSelector(selectShouldGetActivityHint)

  const events = useAppSelector(selectBootcampActivityEvents)
  const didUpdateEvent = useAppSelector(selectDidUpdateEvent)
  const answerState = useAppSelector(selectAnswerState)
  const energyOrbsChance = useWillGetEnergyOrbs()
  const energyOrb = useAppSelector(selectShouldGetEnergyOrbs)
  const cheat = useAppSelector(selectShouldAutoValidateAnswer)
  const shouldShowAutoValidateAnswer = isCheatFunctionalityEnabled()

  const correctConsecutiveAnswers = useAppSelector(selectCorrectConsecutiveAnswers)

  const bootcampComplexityLevel = useAppSelector(selectVccComplexityLevel) || 1
  const activityType = useAppSelector(selectCurrentActivityType)
  const currentRound = useAppSelector(selectCurrentRound)
  const [difficultyLevel] = useGetCurrentDifficultyLevel(events, activities)
  const rewardLevel = useGetCurrentRewardLevel(events, activities)
  const gemName = useGetGemType(activities)
  const activityWorkloadLevel = useGetActivityWorkloadLevel(activities)
  const router = useRouter()

  const questionType = getActivityProperty(activities, 'questionType', activityType, difficultyLevel)
  const activityName = getActivityProperty(activities, 'activityName', activityType, difficultyLevel)
  const activityDesc = getActivityProperty(activities, 'description', activityType, difficultyLevel)
  const activityDiffLevelsCount = getActivityDiffLevelsCount(activities, activityType)
  const currentActivitySet = activities.find((elem) => elem.type === activityType)

  const rewardsCollectedAtStartOfSession = useRef(0)
  const rewardsCollectedDuringSession = useRef(0)
  const collectedRewards = useGetCollectedRewards(events)
  const availableRewards = useGetAvailableRewards(activities)

  const shouldDisableUserInteraction = useAppSelector(selectShouldDisableUserInteraction)

  const colorInfo: CategoryColorClasses = useAppSelector(selectVideoTopicColor)

  const showRoundSummary = useShowRoundSummary()

  const dispatch = useAppDispatch()
  const analyticsEventName = [subtopic, bootcampKey, moduleKey, level, action, activityName].join(':')
  const [lockAnswerState, setLockAnswerState] = useState<boolean>(false) // Here we locking Answer State once Answer is true
  const lockStoreEventRef = useRef<boolean>(true)
  const activityWrapperRef = useRef<HTMLDivElement>(null)
  const [isDataLoading, setIsDataLoading] = useState<boolean>(false)

  const authenticated = useAppSelector(isAuthenticated)

  const rewards = useGetActivityReward()

  const [setActivityRound] = useSetActivityRoundMutation()

  const hasRun = useRef(false)

  useEffect(() => {
    if (!hasRun.current && currentActivitySet) {
      const calculatedKudos = Math.floor(Math.random() * (currentActivitySet.difficulties.length + 1))

      dispatch(setKudosActionCreator(calculatedKudos))
      hasRun.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const captureScreenshot = async (): Promise<void> => {
      if (!activityWrapperRef.current) return

      // hide elements that should not be included in the screenshot
      const elementsToHide = document.querySelectorAll('.exclude-from-screenshot')
      elementsToHide.forEach((el) => ((el as HTMLElement).style.display = 'none'))

      const imgData = await toJpeg(activityWrapperRef.current, { cacheBust: true, backgroundColor: 'white' })

      // restore hidden elements
      elementsToHide.forEach((el) => ((el as HTMLElement).style.display = ''))

      sendToOracleAI(activityDesc ? JSON.stringify(activityDesc) : '', imgData, questionType, [])
    }

    if (activityHint) {
      captureScreenshot()
      dispatch(setShouldGetActivityHintActionCreator(false))
    }
  }, [activityDesc, activityHint, dispatch, questionType, sendToOracleAI])

  // fetch firebase data and update relevant fields in redux store
  useEffect(() => {
    async function fetchEvents(): Promise<void> {
      if (!userId) {
        return
      }
      setLockAnswerState(false)
      lockStoreEventRef.current = true
      dispatch(resetCorrectConsecutiveAnswersActionCreator())
      setIsDataLoading(true)

      try {
        const { query, fireStore, getDocs, collection, where } = await getFirebase()
        const data = await getDocs(
          query(
            collection(fireStore, 'bootcamp', String(userId), 'events'),
            where('bootcampId', '==', bootcampKey),
            where('moduleId', '==', moduleKey),
            where('levelId', '==', level),
            where('levelPage', '==', LevelActionType.Activity),
            where('type', '==', activityType),
          ),
        )

        const roundData = await getDocs(
          query(
            collection(fireStore, 'bootcamp', String(userId), 'events'),
            where('bootcampId', '==', bootcampKey),
            where('moduleId', '==', moduleKey),
            where('levelId', '==', level),
            where('levelPage', '==', LevelActionType.Round),
            where('type', '==', String(activityType)),
          ),
        )

        if (data && roundData) {
          const eventIds = data.docs.map((elem) => elem.id) as BootcampActivityEventId[]
          const roundEventIds = roundData.docs.map((elem) => elem.id) as BootcampActivityRoundEventId[]
          rewardsCollectedAtStartOfSession.current = eventIds.length
          dispatch(setBootcampActivityEventsActionCreator(eventIds))
          dispatch(setCurrentRoundActionCreator(getCurrentRoundFromRoundEvents(roundEventIds)))
          dispatch(setCollectedRewardsCreator(eventIds.length))
        } else {
          dispatch(setCollectedRewardsCreator(0))
        }
        setIsDataLoading(false)
      } catch (e) {
        setIsDataLoading(false)
        dispatch(setCollectedRewardsCreator(0))
      }
    }

    fetchEvents()
  }, [activityType, bootcampKey, dispatch, level, moduleKey, userId])

  useEffect(() => {
    dispatch(resetCorrectConsecutiveAnswersActionCreator()) // Cleared all consecutive answer history
    dispatch(setAnswerStateActionCreator(null)) // Reset answer state, null means not attempted.
  }, [dispatch, level])

  useEffect(() => {
    dispatch(setCurrDifficultyLevelActionCreator(difficultyLevel))
    dispatch(setCurrRewardLevelActionCreator(rewardLevel))
  }, [dispatch, difficultyLevel, rewardLevel])

  // analytics
  const trackActivityAnswer = useCallback(
    (isCorrect: boolean) => {
      bootcampAnalytics.log(BCAEvent.CHECK_ANSWER, {
        status: isCorrect ? Labels.CORRECT : Labels.INCORRECT,
        category: subtopic,
        subject: bootcampKey,
        bootcamp: moduleKey,
        level,
        bootcamp_page_type: action,
        activity_name: activityName,
      })
    },
    [action, activityName, bootcampKey, level, moduleKey, subtopic],
  )

  useEffect(() => {
    if (answerState === true) {
      dispatch(incrementCorrectConsecutiveAnswersActionCreator())
      trackActivityAnswer(true)
    } else if (answerState === false) {
      dispatch(resetCorrectConsecutiveAnswersActionCreator())
      trackActivityAnswer(false)
    }
  }, [answerState, dispatch, trackActivityAnswer])

  useEffect(() => {
    if (rewardsCollectedDuringSession.current > 0) {
      dispatch(
        setRewardsCountActionCreator({
          initial: rewardsCollectedAtStartOfSession.current,
          collected: rewardsCollectedDuringSession.current,
          available: availableRewards,
        }),
      )

      rewardsCollectedDuringSession.current = 0
    }
  }, [activityType, availableRewards, bootcampKey, level, moduleKey, dispatch])

  // set data to firebase
  useEffect(() => {
    if (!lockStoreEventRef.current && correctConsecutiveAnswers === 3 && collectedRewards < availableRewards) {
      lockStoreEventRef.current = true

      let gemCount = 0
      let xpCount = 0
      let awardMathEnergyOrbs = 0

      const isAtLastDifficultyLevel = collectedRewards + 1 === availableRewards
      if (isAtLastDifficultyLevel) {
        gemCount = rewards.gems
        xpCount = rewards.xp
        awardMathEnergyOrbs = rewards.kudos
      } else {
        xpCount = Math.round(getXPCountInChest(bootcampComplexityLevel, activityWorkloadLevel, difficultyLevel) * 0.75)
      }

      setBootcampData({
        bootcampId: bootcampKey,
        moduleId: moduleKey,
        levelId: level,
        levelPage: action,
        round: currentRound,
        activity: {
          type: activityType,
          difficultyLevel,
          reward: rewardLevel,
          gem: gemName,
          gemCount,
          xp: xpCount,
        },
        awardMathEnergyOrbs,
        gymType: subtopic,
      })
      if (urlQuery?.showGrade) {
        dispatch(setChecklistNotification({ rewardFeature: REWARD_FEATURE.COMPLETE_CURRICULUM_ACTIVITY }))
      }
      bootcampAnalytics.logGamification(
        {
          category: subtopic,
          subject: bootcampKey,
          bootcamp: moduleKey,
          level,
          bootcamp_page_type: action,
          activity_name: activityName,
        },
        gemCount,
        xpCount,
        energyOrb ? 1 : 0,
      )
      rewardsCollectedDuringSession.current += 1
      dispatch(setCollectedRewardsCreator(collectedRewards + 1))
    }
  }, [
    action,
    activityType,
    availableRewards,
    analyticsEventName,
    bootcampKey,
    collectedRewards,
    correctConsecutiveAnswers,
    bootcampComplexityLevel,
    activityWorkloadLevel,
    difficultyLevel,
    dispatch,
    energyOrb,
    gemName,
    level,
    lockStoreEventRef,
    moduleKey,
    rewardLevel,
    setBootcampData,
    subtopic,
    activityName,
    currentRound,
    urlQuery,
    currentActivitySet,
    rewards.gems,
    rewards.xp,
    rewards.kudos,
  ])

  const handleOnNextQuestionClick = useCallback(() => {
    if (!didUpdateEvent && correctConsecutiveAnswers === 3) {
      let gemCount = 0
      let xpCount = 0

      const isAtLastDifficultyLevel = collectedRewards + 1 === availableRewards

      if (isAtLastDifficultyLevel) {
        gemCount = rewards.gems
        xpCount = rewards.xp
      } else {
        xpCount = Math.round(getXPCountInChest(bootcampComplexityLevel, activityWorkloadLevel, difficultyLevel) * 0.75)
      }
      const eventId = constructBootcampActivityEventId(
        bootcampKey as BootcampKey,
        moduleKey,
        level,
        activityType,
        difficultyLevel,
        rewardLevel,
        gemCount,
        xpCount,
        currentRound,
      )

      if (isAtLastDifficultyLevel) {
        rewardsCollectedDuringSession.current = 0
        dispatch(setNextBootcampPageActionCreator({ currentActivityType: activityType }))

        // End current round
        setActivityRound({
          activityPath: asPath,
          product: 'bootcamp',
          end: true,
          roundId: String(currentRound),
        })
      }

      dispatch(setBootcampActivityEventsActionCreator(eventId))
      dispatch(setDidUpdateEventActionCreator(true))
    }

    if (correctConsecutiveAnswers === 3) {
      if (difficultyLevel < activityDiffLevelsCount) {
        dispatch(incrementDifficultyLevelActionCreator())
      }

      if (collectedRewards < availableRewards) {
        dispatch(incrementRewardLevelActionCreator())
      }

      dispatch(resetCorrectConsecutiveAnswersActionCreator())
      dispatch(setDidUpdateEventActionCreator(false))
    } else if (correctConsecutiveAnswers === 2 && collectedRewards < availableRewards) {
      dispatch(setShouldGetEnergyOrbsActionCreator(energyOrbsChance))
    }

    setLockAnswerState(false)
    lockStoreEventRef.current = correctConsecutiveAnswers !== 2
    dispatch(setAnswerStateActionCreator(null))
  }, [
    didUpdateEvent,
    correctConsecutiveAnswers,
    collectedRewards,
    availableRewards,
    dispatch,
    bootcampKey,
    moduleKey,
    level,
    activityType,
    difficultyLevel,
    rewardLevel,
    currentRound,
    rewards.gems,
    rewards.xp,
    bootcampComplexityLevel,
    activityWorkloadLevel,
    setActivityRound,
    asPath,
    activityDiffLevelsCount,
    energyOrbsChance,
  ])

  const activityLogger = useBootcampActivityLogging({
    questionType,
    subtopic,
    bootcampKey,
    moduleKey,
    level,
    activityType,
    activityName,
  })

  const handleOnAnswerStateChange = useCallback(
    (answer: boolean | null, questionId: string, bigActivity?: boolean) => {
      const value = cheat ? true : answer
      if (cheat && !questionId && questionType !== ActivityQuestionType.interactive) return

      if (typeof value === 'boolean') {
        if (!userId) {
          dispatch(setShowFreemiumModalCreator(true))
          return
        } else {
          activityLogger(value, questionId)
        }
      }

      if (lockAnswerState) return

      if (bigActivity) lockStoreEventRef.current = false

      if (value) {
        setLockAnswerState(true)
      }

      dispatch(setAnswerStateActionCreator(value))
    },
    [cheat, questionType, lockAnswerState, dispatch, userId, activityLogger],
  )

  const onNextQuestionClick = (): void => {
    dispatch(setHideHorizontalMenuActionCreator(false))
  }
  const renderActivityByType = (): React.ReactElement => {
    if (questionType === ActivityQuestionType.interactive && activityName) {
      return (
        <Activities
          actionButtonsFC={CheckAnswerAndClearButton}
          activityName={activityName}
          activityType={activityType}
          answerState={answerState}
          autofocus
          correctConsecutiveAnswersCount={correctConsecutiveAnswers}
          difficultyLevel={difficultyLevel}
          levelId={level}
          onAnswerStateChange={handleOnAnswerStateChange}
          onNextQuestionClick={handleOnNextQuestionClick}
        />
      )
    } else {
      return (
        <SanityActivityRenderer
          actionButtonsFC={CheckAnswerAndClearButton}
          answerState={answerState}
          levelId={level}
          onAnswerStateChange={handleOnAnswerStateChange}
          onNextQuestionClick={handleOnNextQuestionClick}
        />
      )
    }
  }

  const isGradePage = useIsBootcampGradePage()
  const nextActivityType = useNextActivityType()

  // Hide continue button on grade page if next activity is not available and if curriculum level
  const isShowGrade = router.query?.showGrade === 'true'
  const hideContinueButton = (isGradePage && nextActivityType === 0) || isShowGrade

  return (
    <>
      <section className="flex-1 flex flex-col" data-testid="activity-page-content" ref={activityWrapperRef}>
        <Loader className="mx-auto my-40 md:mt-40" isLoading={isDataLoading}>
          <Fragment>
            {!showRoundSummary && (
              <ProgressBarChest
                availableRewards={availableRewards}
                collectedRewards={collectedRewards}
                correctConsecutiveAnswers={correctConsecutiveAnswers}
              />
            )}
            <form
              className={`flex-grow flex flex-col justify-center ${
                shouldDisableUserInteraction ? 'pointer-events-none' : ''
              }`}
              onSubmit={(event) => {
                event.preventDefault()
              }}
            >
              {showRoundSummary ? (
                <div className="min-h-sm">
                  <ActivitySetCompleted />
                </div>
              ) : (
                <>
                  {activityDesc && <BlockContent blocks={activityDesc} className={`${styles['sanity-block']}`} />}
                  {renderActivityByType()}

                  {hideContinueButton ? null : (
                    <NextLevel
                      borderClass={`border-2 ${colorInfo.borderClass}`}
                      className={colorInfo.textClass}
                      iconColor={colorInfo.textClass}
                      onClick={onNextQuestionClick}
                      wrapperClass="mt-4 mx-auto exclude-from-screenshot"
                    />
                  )}

                  {authenticated && (
                    <div className="absolute flex md:flex-col-reverse gap-2 md:justify-start bottom-2.5 left-3 md:left-4 md:bottom-3  exclude-from-screenshot">
                      <FeedbackButton
                        contentId={`${bootcampKey}#${moduleKey}#${level}#${action}#${activityType}`}
                        contentSubtype={questionType}
                        contentTitle={activityName}
                        product={FeedbackProducts.vcc.activity}
                      />
                      {shouldShowAutoValidateAnswer && (
                        <div className="flex md:flex-col items-center">
                          <p className="pr-2 my-auto text-xs">Cheat</p>
                          <ToggleButton
                            onChange={(v) => {
                              dispatch(setShouldAutoValidateAnswerActionCreator(v))
                            }}
                            value={cheat}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </form>
          </Fragment>
        </Loader>
      </section>
    </>
  )
}
